import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiff } from '../hooks/useLiff';
import { staffApiService } from '../services/staffApi';
import { useLanguage } from '../contexts/LanguageContext';
import { useStaff } from '../contexts/StaffContext';
import AnalogClock from './AnalogClock';
import CustomDatePicker from './CustomDatePicker';

const StaffDashboard = () => {
  const { liff, isLoggedIn, idToken } = useLiff();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { staffUser, loading: staffLoading, error: staffError, isStaff } = useStaff();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    if (staffUser && isStaff) {
      fetchDashboardData();
    }
  }, [selectedDate, staffUser, isStaff]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const dateStr = selectedDate.toISOString().split('T')[0];
      const data = await staffApiService.getDashboardOverview(staffUser.idToken, dateStr);
      setDashboardData(data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const processTimeSlotsForCourt = (court, showAllTimeSlots = false) => {
    const { openingHours, bookedSlots } = court;
    
    // Generate time slots based on this court's opening hours
    const generateTimeSlots = () => {
      const timeSlots = [];
      const [openHour, openMin] = openingHours.open.split(':').map(Number);
      const [closeHour, closeMin] = openingHours.close.split(':').map(Number);
      
      let currentHour = openHour;
      let currentMinute = openMin;
      
      while (currentHour < closeHour || (currentHour === closeHour && currentMinute < closeMin)) {
        const timeString = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
        timeSlots.push(timeString);
        
        currentMinute += 30;
        if (currentMinute >= 60) {
          currentMinute = 0;
          currentHour++;
        }
      }
      
      return timeSlots;
    };
    
    const timeSlots = generateTimeSlots();
    
    // Check if selected date is today
    const isToday = selectedDate.toDateString() === new Date().toDateString();
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    
    return timeSlots.map(timeSlot => {
      const [hours, minutes] = timeSlot.split(':').map(Number);
      const slotMinutes = hours * 60 + minutes;
      
      // Check if within opening hours
      const [openHour, openMin] = openingHours.open.split(':').map(Number);
      const [closeHour, closeMin] = openingHours.close.split(':').map(Number);
      const openMinutes = openHour * 60 + openMin;
      const closeMinutes = closeHour * 60 + closeMin;
      
      let status = 'closed'; // Default to closed
      let customerDisplayName = null;
      let customerBookingName = null;
      
      if (slotMinutes >= openMinutes && slotMinutes < closeMinutes) {
        // Check if it's today and time has passed (only if showAllTimeSlots is false)
        if (!showAllTimeSlots && isToday && hours < currentHour) {
          status = 'closed'; // Past time
        } else if (!showAllTimeSlots && isToday && hours === currentHour && minutes <= currentMinute) {
          status = 'closed'; // Past time
        } else {
          // Check if this slot is booked
          const bookedSlot = bookedSlots.find(slot => {
            const slotStartMinutes = slotMinutes;
            const slotEndMinutes = slotMinutes + 30; // Each slot is 30 minutes
            const bookedStartMinutes = timeToMinutes(slot.startTime);
            const bookedEndMinutes = timeToMinutes(slot.endTime);
            
            // Check if there's any overlap
            return !(slotEndMinutes <= bookedStartMinutes || slotStartMinutes >= bookedEndMinutes);
          });
          
          if (bookedSlot) {
            status = 'booked';
            customerDisplayName = bookedSlot.customerDisplayName;
            customerBookingName = bookedSlot.customerBookingName;
          } else {
            status = 'available';
          }
        }
      }
      
      return {
        time: timeSlot,
        status,
        customerDisplayName,
        customerBookingName
      };
    });
  };

  const handleLogout = async () => {
    try {
      if (liff) {
        liff.logout();
      }
      navigate('/');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/');
    }
  };

  // Show loading while checking permissions
  if (staffLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังตรวจสอบสิทธิ์...</p>
        </div>
      </div>
    );
  }

  // Show error if not authorized
  if (staffError || (!staffUser && !staffLoading)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">ไม่มีสิทธิ์เข้าถึง</h1>
          <p className="text-gray-600 mb-6">{staffError || 'คุณไม่มีสิทธิ์เข้าถึงหน้านี้'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            กลับหน้าหลัก
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">📊 Business Dashboard</h1>
              <span className="ml-3 px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                พนักงาน
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                สวัสดี, {staffUser?.displayName || 'ผู้ใช้'}
              </span>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
              >
                ออกจากระบบ
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          {/* Date Selection */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">ภาพรวมการจองสนาม</h2>
                <p className="text-gray-600 mt-1">ตรวจสอบสถานะการจองสนามทั้งหมด</p>
              </div>
              
              <div className="flex items-center gap-4">
                <CustomDatePicker
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={(dateStr) => setSelectedDate(new Date(dateStr))}
                  className="w-48"
                />
                <button
                  onClick={fetchDashboardData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  🔄 Refresh
                </button>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900">
                {formatDate(selectedDate)}
              </h3>
            </div>
          </div>

          {/* Dashboard Content */}
          {dashboardData && dashboardData.courtTypes ? (
            <div className="space-y-8">
              {dashboardData.courtTypes.map((courtType) => (
                <div key={courtType.id} className="bg-white rounded-lg shadow">
                  {/* Court Type Header */}
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl">
                        {courtType.id === 'basketball' && '🏀'}
                        {courtType.id === 'football' && '⚽'}
                        {courtType.id === 'badminton' && '🏸'}
                        {courtType.id === 'tennis' && '🎾'}
                        {courtType.id === 'pickleball' && '🥒'}
                        {courtType.id === 'studio' && '🎭'}
                        {!['basketball', 'football', 'badminton', 'tennis', 'pickleball', 'studio'].includes(courtType.id) && '🏟️'}
                      </span>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{courtType.thaiName}</h3>
                        <p className="text-gray-600">{courtType.name}</p>
                      </div>
                    </div>
                  </div>

                  {/* Courts Grid */}
                  <div className="p-6">
                    <div className="overflow-x-auto">
                      <div className="flex gap-6 pb-4" style={{ minWidth: 'max-content' }}>
                        {courtType.courts.map((court) => (
                          <div key={court.id} className="flex-shrink-0">
                            <AnalogClock 
                              court={court} 
                              selectedDate={selectedDate}
                              timeSlotsData={processTimeSlotsForCourt(court, true)}
                              showCustomerNames={true}
                              showAllTimeSlots={true}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600">ไม่พบข้อมูลการจองสำหรับวันที่เลือก</p>
            </div>
          )}

          {/* Legend */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">คำอธิบาย</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                <span className="text-gray-700">ว่าง - สามารถจองได้</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                <span className="text-gray-700">จองแล้ว - มีลูกค้าจอง</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-gray-300 rounded-full"></div>
                <span className="text-gray-700">ปิด - ไม่อยู่ในช่วงเวลาเปิด</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;
